/**
 * Polling configuration and utilities for task/batch status polling.
 *
 * Uses adaptive exponential backoff with jitter to minimize server load
 * while providing responsive updates.
 */

import { getRateLimitDelay } from './rate-limit'

export interface PollingConfig {
  /** Starting interval in ms (default: 1000) */
  initialInterval: number
  /** Maximum interval in ms (default: 30000) */
  maxInterval: number
  /** Multiplier for each poll attempt (default: 1.5) */
  backoffFactor: number
  /** Randomization percentage +-% (default: 20) */
  jitterPercent: number
}

/** Default config for individual task polling */
export const DEFAULT_TASK_POLLING_CONFIG: PollingConfig = {
  initialInterval: 1000, // Start at 1 second
  maxInterval: 30000, // Cap at 30 seconds
  backoffFactor: 1.5,
  jitterPercent: 20,
}

/** Config for batch status polling (slower, gentler backoff) */
export const DEFAULT_BATCH_POLLING_CONFIG: PollingConfig = {
  initialInterval: 3000, // Start at 3 seconds
  maxInterval: 15000, // Cap at 15 seconds
  backoffFactor: 1.2,
  jitterPercent: 20,
}

/**
 * Calculate the next polling interval using exponential backoff with jitter.
 *
 * Example progression with default task config:
 * - Attempt 0: ~1000ms
 * - Attempt 1: ~1500ms
 * - Attempt 2: ~2250ms
 * - Attempt 3: ~3375ms
 * - ...caps at 30000ms
 *
 * @param attempt - The current attempt number (0-indexed)
 * @param config - Polling configuration
 * @returns Interval in milliseconds
 */
export function calculatePollingInterval(
  attempt: number,
  config: PollingConfig = DEFAULT_TASK_POLLING_CONFIG
): number {
  // Calculate base interval with exponential backoff
  const base = Math.min(
    config.maxInterval,
    config.initialInterval * Math.pow(config.backoffFactor, attempt)
  )

  // Apply jitter: interval * (1 - jitter/100 + random * jitter/50)
  // This gives us a range of [base * 0.8, base * 1.2] for 20% jitter
  const jitterMultiplier =
    1 - config.jitterPercent / 100 + Math.random() * ((config.jitterPercent * 2) / 100)

  return Math.round(base * jitterMultiplier)
}

/**
 * Calculate the effective polling interval, respecting any active rate limit.
 * If rate-limited, returns the remaining delay instead of the normal backoff interval.
 */
export function getEffectivePollingInterval(
  attempt: number,
  config: PollingConfig = DEFAULT_TASK_POLLING_CONFIG
): number {
  const rateLimitDelay = getRateLimitDelay()
  if (rateLimitDelay > 0) return rateLimitDelay
  return calculatePollingInterval(attempt, config)
}

/**
 * Check if a status indicates the task/batch is still active.
 */
export function isActiveStatus(status: string): boolean {
  return status === 'pending' || status === 'processing'
}

/**
 * Check if a status indicates the task/batch is terminal (completed, failed, or cancelled).
 */
export function isTerminalStatus(status: string): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled'
}
